import { t } from "@/i18n-js/instance";
import type { BaseBlock } from "@/react/components/shared/BuilderUI/DataModel.types";
import type { ElementType } from "@/react/types/Element";

import { blockConverters } from "..";

const getCanvasName = (label: string): string => {
  const suffix = t("builder_ui.editor.profile_field_suffix");
  return `${label} ${suffix}`;
};

// Converts a form element to a BuilderUI block
export const elementToBlock = (
  element: ElementType,
  fieldIndex?: number,
  skipProfileFieldTypes = false,
): BaseBlock => {
  switch (element.type) {
    case "field": {
      const block = (() => {
        switch (element.attributes.field_type) {
          case "contact_name":
            return blockConverters.FormContactNameBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "contact_email":
            return blockConverters.FormContactEmailBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "short_answer":
            return blockConverters.FormShortAnswerBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "link":
            return blockConverters.FormLinkBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "long_answer":
            return blockConverters.FormLongAnswerBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "single_choice":
            return blockConverters.FormSingleChoiceBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "multiple_choice":
            return blockConverters.FormMultipleChoiceBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "dropdown":
            return blockConverters.FormDropdownBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "number":
            return blockConverters.FormNumberBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          case "email":
            return blockConverters.FormEmailBlock.elementToBlock(
              element,
              fieldIndex ?? 0,
            );
          default:
            throw new Error(`Unknown field type`);
        }
      })();

      // If this field is linked to a profile field, update the block type
      // Skip this in renderer context where we just need the underlying field type
      if (element.attributes.profile_field_id && !skipProfileFieldTypes) {
        return {
          ...block,
          type: `ProfileField_${element.attributes.profile_field_id}`,
          meta: {
            ...block.meta,
            fieldType: element.attributes.field_type,
            canvasName: getCanvasName(element.attributes.label),
          },
        };
      }

      return block;
    }

    case "text": {
      const textType = element.attributes.text_type;
      if (textType === "title" || textType === "subtitle") {
        return blockConverters.FormHeadingBlock.elementToBlock(element);
      }
      return blockConverters.FormParagraphBlock.elementToBlock(element);
    }

    case "button":
      return blockConverters.FormButtonBlock.elementToBlock(element);

    case "layout":
      return blockConverters.FormStackBlock.elementToBlock(element);

    default:
      throw new Error("Unknown element type");
  }
};
